import { useResponsive } from '@mntn/ui-components'
import { formatMoney } from '@/components/shared/utils'
import type { OrderItemsStatusFilter } from '@/flight-deck-api/services/order-items.service'
import { useOrderItemsSummary } from '@/hooks'
import type { TransformedOrderItemFilters } from '../hooks/use-transformed-order-item-filters'
import { SummaryCard, SummaryCardSkeleton } from './summary-card'

interface SummarySectionProps {
  status: OrderItemsStatusFilter
  search?: string
  filters?: TransformedOrderItemFilters
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const SummarySection: React.FC<SummarySectionProps> = ({ status, search, filters }) => {
  const { isLessThan } = useResponsive()

  // Fetch order item summary (React Query will dedupe with Dashboard's query)
  const { orderItemSummary, isLoading, isError } = useOrderItemsSummary({
    status,
    search,
    filters,
  })

  // Show skeleton for loading or error states (graceful degradation)
  if (isLoading || isError || !orderItemSummary) {
    return <SummarySectionSkeleton />
  }

  const { upcomingDeals, budget, liveCampaigns } = orderItemSummary
  const isCompactLayout = isLessThan('lg')

  return (
    <div className="py-2 px-6 mb-6 border-y border-border w-full mx-auto">
      <div
        className={
          isCompactLayout ? 'grid grid-cols-2 gap-0 w-full' : 'flex flex-row items-center justify-between w-full'
        }
      >
        <SummaryCard
          titleText="Budget Allocated"
          value={budget.effective}
          total={budget.total}
          unit="Budget"
          formatValue={val => formatMoney(val)}
          className={
            isCompactLayout ? 'border-r border-b border-border' : 'border-r border-border last:border-none flex-1'
          }
        />
        <SummaryCard
          titleText="Spend"
          value={budget.delivered}
          total={budget.effective}
          unit="Spend"
          formatValue={val => formatMoney(val)}
          className={isCompactLayout ? 'border-b border-border' : 'border-r border-border last:border-none flex-1'}
        />
        <SummaryCard
          titleText="Deals Fully Allocated"
          value={upcomingDeals.effective}
          total={upcomingDeals.total}
          unit="Deals"
          className={isCompactLayout ? 'border-r border-border' : 'border-r border-border last:border-none flex-1'}
        />
        <SummaryCard
          titleText="Live Campaigns"
          value={liveCampaigns}
          unit="Campaigns"
          className={isCompactLayout ? '' : 'border-r border-border last:border-none flex-1'}
        />
      </div>
    </div>
  )
}

const SummarySectionSkeleton = () => {
  const { isLessThan } = useResponsive()
  const isCompactLayout = isLessThan('lg')

  const getSkeletonClassName = (index: number) => {
    if (isCompactLayout) {
      // 2x2 grid layout
      if (index === 0) return 'border-r border-b border-border' // top-left
      if (index === 1) return 'border-b border-border' // top-right
      if (index === 2) return 'border-r border-border' // bottom-left
      return '' // bottom-right
    }
    // Single row layout
    return 'border-r border-border last:border-none flex-1'
  }

  return (
    <div className="py-2 px-6 mb-6 border-y border-border w-full mx-auto">
      <div
        className={
          isCompactLayout ? 'grid grid-cols-2 gap-0 w-full' : 'flex flex-row items-center justify-between w-full'
        }
      >
        {[
          { titleClassName: 'w-[163px]', valueClassName: 'w-[357px]' },
          { titleClassName: 'w-[54px]', valueClassName: 'w-full' },
          { titleClassName: 'w-[203px]', valueClassName: 'w-[57px]' },
          { titleClassName: 'w-[133px]', valueClassName: 'w-[34px]' },
        ].map((skeleton, index) => (
          <div key={index} className={`min-w-0 ${getSkeletonClassName(index)}`}>
            <SummaryCardSkeleton {...skeleton} />
          </div>
        ))}
      </div>
    </div>
  )
}
